import React, { FC } from "react";

import { DataViewKey, KpkMedia, useDataViewElement, useShare } from "@keepeek/commons";
import {
  AttachmentActions,
  AttachmentMassActions,
  AttachmentsPanel,
  DisplayMode,
  ElementsMetaField,
  FormatElementId,
  FormatTechnicalFields,
} from "@keepeek/refront-components";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import * as Sentry from "@sentry/nextjs";
import clsx from "clsx";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { KIcon } from "../../components/common/KIcon";
import { CONFIGURATION_SECTION_PATH } from "../../components/layout/adminMenu";
import { DownloadManager as DownloadManagerConf } from "../../models/configuration/components/downloadManager";
import { useAttachments } from "../../providers/attachment/hooks/attachments";
import { useConfiguration } from "../../providers/config/hooks";
import { configSectionPageSearchSelector } from "../../providers/config/selectors";
import { DownloadManagerAttachmentsByElement } from "../../providers/downloadManager/atoms/attachments";

const PREFIX = "AttachmentsByElement";

const classes = {
  root: `${PREFIX}-root`,
  element: `${PREFIX}-element`,
  attachments: `${PREFIX}-attachments`,
  media: `${PREFIX}-media`,
  elementContent: `${PREFIX}-elementContent`,
  elementContentFields: `${PREFIX}-elementContentFields`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: "grid",
    gridTemplateAreas: `"element" "attachments"`,
    gridTemplateColumns: "1fr",
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.primary.main}`,
    marginBottom: theme.spacing(2),
    overflow: "hidden",
  },

  [`& .${classes.element}`]: {
    flexGrow: 0,
    gridArea: "element",
    borderRadius: `${theme.shape.borderRadius}px 0 0 ${theme.shape.borderRadius}px`,
    display: "grid",
    gridAutoFlow: "column",
    gridTemplateColumns: `1fr 3fr`,
  },

  [`& .${classes.attachments}`]: {
    gridArea: "attachments",
    borderRadius: `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0`,
    backgroundColor: theme.palette.grey[200],
  },

  [`& .${classes.media}`]: {
    gridArea: "1/1",
    flexShrink: 0,
    "& img": {
      display: "block" /* avoid inline image space */,
      width: "100%",
      height: "100%",
    },
  },

  [`& .${classes.elementContent}`]: {
    padding: theme.spacing(1),
    display: "flex",
    alignItems: "flex-end",
    minWidth: "0",
  },

  /**
   * Needed for ellipsis to work on fields, see https://css-tricks.com/flexbox-truncated-text/
   */
  [`& .${classes.elementContentFields}`]: {
    minWidth: "0",
  },
}));

export type AttachmentsByElementProps = {
  attachmentsByElement: DownloadManagerAttachmentsByElement;
  elements: KpkMedia[];
  dataViewKey: DataViewKey;
};

const AttachmentsByElement: FC<React.PropsWithChildren<AttachmentsByElementProps>> = function ({
  attachmentsByElement,
  elements,
  dataViewKey,
}) {
  const { elementId } = useRecoilValue(configSectionPageSearchSelector) ?? { elementId: undefined };

  const { enableOtherFormatsMassActions } = useConfiguration<DownloadManagerConf>(
    CONFIGURATION_SECTION_PATH.COMPONENTS_DOWNLOAD_MANAGER,
  ) ?? { enableOtherFormatsMassActions: undefined };

  const element = elements.find((e) => e.id === attachmentsByElement.elementId);
  const router = useRouter();
  const { isUnitaryShareElement } = useShare(router.pathname, router.query.tokenId);
  const { functionalFieldsToDisplay, technicalFieldsToDisplay } = useDataViewElement({
    key: dataViewKey,
    id: attachmentsByElement.elementId,
    isUnitaryShareElement,
    sentryCallback: ({ category, message }) => Sentry.addBreadcrumb({ category, message }),
  });

  const { t: tRefrontComponents } = useTranslation("refront-components");

  const { onDownloadAttachment, onDownloadAttachments, copyAttachment, copyAttachments, loading } =
    useAttachments({ elementId: attachmentsByElement.elementId });

  const fieldsElementBox = () => {
    if (element) {
      return (
        <Box className={classes.element}>
          <Box className={classes.media}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img onContextMenu={(e) => e.preventDefault()} src={element.previewUrl} alt={title} />
          </Box>
          <Box className={classes.elementContent}>
            <Box className={classes.elementContentFields}>
              <FormatElementId element={element} elementIdRules={elementId} />
              <ElementsMetaField
                fields={functionalFieldsToDisplay}
                media={element}
                dataViewKey={dataViewKey}
              />
              <FormatTechnicalFields
                element={element}
                technicalFieldsToDisplay={technicalFieldsToDisplay}
                folderDisplayMode={DisplayMode.FIRST_FOLDER_WITHOUT_LINK}
                KIcon={KIcon}
              />
            </Box>
          </Box>
        </Box>
      );
    }
    return (
      <Box className={classes.element}>
        <Box className={classes.media}>
          {tRefrontComponents("mediafield_id")} : {attachmentsByElement.elementId}
        </Box>
      </Box>
    );
  };

  const title: string = (!Array.isArray(element?.title?.value) && element?.title?.value) || "";

  return (
    <StyledBox className={clsx("TypeByElement", classes.root)} component="li">
      {fieldsElementBox()}
      <Box className={classes.attachments}>
        <AttachmentsPanel
          attachments={attachmentsByElement.attachments}
          onAttachmentDownload={onDownloadAttachment}
          onAttachmentCopy={copyAttachment}
          attachmentActionAvailable={[AttachmentActions.Copy, AttachmentActions.Download]}
          onDownloadAll={onDownloadAttachments}
          onCopyAll={copyAttachments}
          attachmentMassActionAvailable={
            enableOtherFormatsMassActions
              ? [AttachmentMassActions.CopyAll, AttachmentMassActions.DownloadAll]
              : []
          }
          copyAllLoading={loading}
          KIcon={KIcon}
        />
      </Box>
    </StyledBox>
  );
};

export default AttachmentsByElement;
